/* ==========================================================================
   Cookie Policy & Privacy Statement Page View (Multilingual - 6 Languages)
   Strictly aligned with authentic audit & GDPR specifications
   Categories: Nezbytné (LocalStorage), Analytické (GA4), Externí média (YouTube)
   ========================================================================== */

import { getLocale } from '../locales/i18n.js';
import { openCookieSettings } from '../components/CookieBanner.js';

const cookiePolicyTranslations = {
  cs: {
    eyebrow: 'OCHRANA SOUKROMÍ A COOKIES',
    title: 'Zásady používání cookies',
    subtitle: 'Informace o používání souborů cookie, místního úložiště a externích technologií na webu jalud.eu.',
    sec1Title: 'Co jsou cookies a místní úložiště?',
    sec1P1: 'Soubory cookie jsou malé textové soubory, které webové stránky ukládají do vašeho prohlížeče při jejich návštěvě. Některé jsou nezbytné pro správné fungování webu, jiné nám po udělení vašeho souhlasu pomáhají vyhodnocovat návštěvnost nebo zobrazovat externí obsah.',
    sec1P2: 'Vedle cookies může web používat také místní úložiště prohlížeče (LocalStorage), například pro zapamatování zvoleného jazyka nebo nastavení cookies.',
    sec2Title: 'Jaké kategorie používáme?',
    catATitle: 'A. Nezbytné technologie',
    catADesc: 'Nezbytné cookies a místní úložiště slouží k základnímu fungování webu, uložení zvoleného jazyka a zapamatování vašeho nastavení ochrany soukromí. Tyto technologie není možné prostřednictvím cookie lišty vypnout, protože bez nich by některé části webu nemusely správně fungovat.',
    catBTitle: 'B. Analytické cookies',
    catBDesc: 'Po udělení souhlasu používáme službu Google Analytics 4 ke statistickému vyhodnocování návštěvnosti a způsobu používání webu. Měřicí značky jsou spravovány prostřednictvím Google Tag Manageru. Pokud analytické cookies odmítnete, analytické cookies se do vašeho zařízení neuloží. Google Consent Mode však může v závislosti na technickém nastavení odesílat omezená měření bez použití cookies.',
    catCTitle: 'C. Externí média',
    catCDesc: 'Web obsahuje videa poskytovaná službou YouTube. Externí obsah se načte až po udělení souhlasu s externími médii. Při načtení nebo přehrání videa může služba YouTube uložit cookies nebo získat informace o vašem zařízení a používání vloženého obsahu. Pokud externí média nepovolíte, zobrazí se místo videa pouze náhled s možností jeho dodatečného povolení.',
    sec3Title: 'Přehled používaných technologií',
    sec3Intro: 'Níže uvedená tabulka obsahuje přehled technologií ověřených technickým auditem webu:',
    thName: 'Název',
    thProvider: 'Poskytovatel',
    thStorageType: 'Typ úložiště',
    thPurpose: 'Účel',
    thCategory: 'Kategorie',
    thDuration: 'Doba platnosti',
    catBadgeNecessary: 'Nezbytné',
    catBadgeAnalytics: 'Analytické',
    catBadgeMedia: 'Externí média',
    consentStorageType: 'Místní úložiště prohlížeče (LocalStorage)',
    consentPurpose: 'Zapamatování nastavení souhlasu s cookies (sed_cookie_consent_v2)',
    consentDuration: 'Trvalé (do smazání v prohlížeči)',
    gaCookieType: 'HTTP Cookie',
    gaPurpose: 'Rozlišení unikátních uživatelů pro statistiky návštěvnosti webu',
    gaDuration: '2 roky (pouze po souhlasu)',
    ga4Purpose: 'Udržování stavu relace a agregace návštěvnosti v Google Analytics 4',
    ga4Duration: '2 roky (pouze po souhlasu)',
    ytCookieType: 'HTTP Cookie / LocalStorage',
    ytPurpose: 'Přehrávání vložených videí a ukládání preferencí přehrávače v režimu zvýšené ochrany soukromí',
    ytDuration: 'Relace až 2 roky (pouze po souhlasu)',
    sec4Title: 'Jak můžete své nastavení změnit?',
    sec4P1: 'Při první návštěvě webu můžete přijmout všechny volitelné technologie, odmítnout je nebo si nastavit jednotlivé kategorie.',
    sec4P2: 'Své rozhodnutí můžete kdykoliv změnit pomocí odkazu „Nastavení cookies“ v zápatí webu nebo kliknutím na tlačítko níže. Odvolání souhlasu nemá vliv na zpracování provedené před jeho odvoláním.',
    changeSettingsBtn: 'Otevřít nastavení cookies',
    sec4P3: 'Cookies a další uložená data můžete odstranit také prostřednictvím nastavení konkrétního webu ve svém internetovém prohlížeči.',
    sec5Title: 'Provozovatel webu a kontakt',
    sec5P1: 'Provozovatel webu a správce osobních údajů:',
    companyName: 'JALUD Embedded s.r.o.',
    companyAddress: 'Nepomucká 1355/261, Černice, 326 00 Plzeň, Česká republika',
    companyId: 'IČO: 03056287',
    contactEmail: 'E-mail: info@jalud-embedded.com',
    privacyLinkText: 'Další informace o zpracování osobních údajů naleznete na stránce',
    privacyPageLink: 'Ochrana soukromí',
    googleLinksIntro: 'Informace třetích stran:',
    googlePrivacyLink: 'Zásady ochrany osobních údajů společnosti Google',
    googleCookieLink: 'Informace společnosti Google o používání cookies a dat'
  },
  en: {
    eyebrow: 'PRIVACY & COOKIES',
    title: 'Cookie Policy',
    subtitle: 'Information about the use of cookies, local storage, and external technologies on jalud.eu.',
    sec1Title: 'What Are Cookies and Local Storage?',
    sec1P1: 'Cookies are small text files placed in your browser when visiting websites. Some are strictly necessary for core functionality, while others help us evaluate site traffic or display external media upon receiving your consent.',
    sec1P2: 'In addition to cookies, the site may use browser local storage (LocalStorage), for instance to remember selected language preferences or cookie consent choices.',
    sec2Title: 'Which Categories Do We Use?',
    catATitle: 'A. Necessary Technologies',
    catADesc: 'Essential cookies and local storage are required for core website operations, remembering language preferences, and storing your privacy choices. These technologies cannot be disabled via the cookie banner because parts of the site cannot function properly without them.',
    catBTitle: 'B. Analytics Cookies',
    catBDesc: 'Upon receiving your consent, we use Google Analytics 4 for statistical evaluation of site traffic and user interactions. Tracking tags are managed through Google Tag Manager. If you decline analytics cookies, no tracking cookies are placed on your device. Depending on configuration, Google Consent Mode may transmit restricted pings without cookies.',
    catCTitle: 'C. External Media',
    catCDesc: 'Our website incorporates videos provided by YouTube. External media content is loaded only after your explicit consent. When loading or playing a video, YouTube may set cookies or collect device information. If external media is not consented to, a placeholder preview is shown with an option to grant consent.',
    sec3Title: 'Overview of Technologies Used',
    sec3Intro: 'The table below details all technologies verified through our technical website audit:',
    thName: 'Name',
    thProvider: 'Provider',
    thStorageType: 'Storage Type',
    thPurpose: 'Purpose',
    thCategory: 'Category',
    thDuration: 'Duration',
    catBadgeNecessary: 'Necessary',
    catBadgeAnalytics: 'Analytics',
    catBadgeMedia: 'External Media',
    consentStorageType: 'Browser Local Storage (LocalStorage)',
    consentPurpose: 'Stores user consent preferences (sed_cookie_consent_v2)',
    consentDuration: 'Persistent (until cleared in browser)',
    gaCookieType: 'HTTP Cookie',
    gaPurpose: 'Distinguishes unique visitors for aggregated traffic analytics',
    gaDuration: '2 years (only after consent)',
    ga4Purpose: 'Maintains session state and metrics in Google Analytics 4',
    ga4Duration: '2 years (only after consent)',
    ytCookieType: 'HTTP Cookie / LocalStorage',
    ytPurpose: 'Enables embedded video playback and player preferences in privacy-enhanced mode',
    ytDuration: 'Session up to 2 years (only after consent)',
    sec4Title: 'How Can You Manage Your Preferences?',
    sec4P1: 'On your first visit, you may accept all optional technologies, decline them, or configure category preferences individually.',
    sec4P2: 'You can change your consent at any time via the "Cookie Settings" link in the website footer or by clicking the button below. Consent revocation does not affect prior lawful processing.',
    changeSettingsBtn: 'Open Cookie Settings',
    sec4P3: 'You can also clear cookies and site data through your browser settings for this domain at any time.',
    sec5Title: 'Website Operator & Contact',
    sec5P1: 'Website Operator and Data Controller:',
    companyName: 'JALUD Embedded s.r.o.',
    companyAddress: 'Nepomucká 1355/261, Černice, 326 00 Pilsen, Czech Republic',
    companyId: 'Company ID (IČO): 03056287',
    contactEmail: 'Email: info@jalud-embedded.com',
    privacyLinkText: 'For detailed information regarding personal data processing, please visit our',
    privacyPageLink: 'Privacy Policy',
    googleLinksIntro: 'Third-party information:',
    googlePrivacyLink: 'Google Privacy Policy',
    googleCookieLink: 'How Google Uses Cookies and Site Data'
  },
  de: {
    eyebrow: 'DATENSCHUTZ & COOKIES',
    title: 'Cookie-Richtlinie',
    subtitle: 'Informationen über die Verwendung von Cookies, lokalem Speicher und externen Technologien auf jalud.eu.',
    sec1Title: 'Was sind Cookies und lokaler Speicher?',
    sec1P1: 'Cookies sind kleine Textdateien, die beim Besuch von Websites im Browser gespeichert werden. Einige sind für den Betrieb unerlässlich, andere helfen uns mit Ihrer Einwilligung bei der Analyse oder der Einbindung externer Inhalte.',
    sec1P2: 'Neben Cookies kann die Website auch den lokalen Speicher des Browsers (LocalStorage) nutzen, beispielsweise zur Speicherung der Sprache oder der Cookie-Präferenzen.',
    sec2Title: 'Welche Kategorien verwenden wir?',
    catATitle: 'A. Notwendige Technologien',
    catADesc: 'Notwendige Cookies und Speicherdaten gewährleisten Kernfunktionen der Website, Sprachauswahl und die Speicherung Ihrer Datenschutzeinstellungen. Diese können nicht deaktiviert werden.',
    catBTitle: 'B. Analyse-Cookies',
    catBDesc: 'Mit Ihrer Einwilligung nutzen wir Google Analytics 4 zur statistischen Auswertung der Website-Nutzung. Die Tags werden über Google Tag Manager verwaltet. Bei Ablehnung werden keine Analyse-Cookies gesetzt.',
    catCTitle: 'C. Externe Medien',
    catCDesc: 'Die Website bindet Videos von YouTube ein. Externe Inhalte werden erst nach Einwilligung geladen. YouTube kann dabei Cookies speichern und Gerätedaten verarbeiten. Ohne Einwilligung wird eine Vorschau angezeigt.',
    sec3Title: 'Übersicht der verwendeten Technologien',
    sec3Intro: 'Die folgende Tabelle zeigt alle durch das technische Audit verifizierten Technologien:',
    thName: 'Name',
    thProvider: 'Anbieter',
    thStorageType: 'Speichertyp',
    thPurpose: 'Zweck',
    thCategory: 'Kategorie',
    thDuration: 'Gültigkeit',
    catBadgeNecessary: 'Notwendig',
    catBadgeAnalytics: 'Analyse',
    catBadgeMedia: 'Externe Medien',
    consentStorageType: 'Lokaler Browserspeicher (LocalStorage)',
    consentPurpose: 'Speichert Cookie-Einwilligungen (sed_cookie_consent_v2)',
    consentDuration: 'Dauerhaft (bis zum Löschen im Browser)',
    gaCookieType: 'HTTP-Cookie',
    gaPurpose: 'Unterscheidung einzelner Besucher für Zugriffsstatistiken',
    gaDuration: '2 Jahre (nur nach Einwilligung)',
    ga4Purpose: 'Sitzungsstatus und Metriken in Google Analytics 4',
    ga4Duration: '2 Jahre (nur nach Einwilligung)',
    ytCookieType: 'HTTP-Cookie / LocalStorage',
    ytPurpose: 'Wiedergabe eingebetteter Videos im erweiterten Datenschutzmodus',
    ytDuration: 'Sitzung bis 2 Jahre (nur nach Einwilligung)',
    sec4Title: 'Wie können Sie Ihre Einstellungen ändern?',
    sec4P1: 'Beim ersten Besuch können Sie alle optionalen Technologien akzeptieren, ablehnen oder individuell anpassen.',
    sec4P2: 'Sie können Ihre Auswahl jederzeit über den Link „Cookie-Einstellungen“ in der Fußzeile oder über die Schaltfläche unten anpassen.',
    changeSettingsBtn: 'Cookie-Einstellungen öffnen',
    sec4P3: 'Sie können gespeicherte Cookies und Websitedaten auch direkt in den Einstellungen Ihres Browsers löschen.',
    sec5Title: 'Website-Betreiber und Kontakt',
    sec5P1: 'Betreiber und Verantwortlicher für die Datenverarbeitung:',
    companyName: 'JALUD Embedded s.r.o.',
    companyAddress: 'Nepomucká 1355/261, Černice, 326 00 Pilsen, Tschechische Republik',
    companyId: 'USt-IdNr. / IČO: 03056287',
    contactEmail: 'E-Mail: info@jalud-embedded.com',
    privacyLinkText: 'Weitere Informationen zur Datenverarbeitung finden Sie in unserer',
    privacyPageLink: 'Datenschutzerklärung',
    googleLinksIntro: 'Drittanbieter-Informationen:',
    googlePrivacyLink: 'Google Datenschutzerklärung',
    googleCookieLink: 'Wie Google Cookies und Daten verwendet'
  },
  es: {
    eyebrow: 'PRIVACIDAD Y COOKIES',
    title: 'Política de cookies',
    subtitle: 'Información sobre el uso de cookies, almacenamiento local y tecnologías externas en jalud.eu.',
    sec1Title: '¿Qué son las cookies y el almacenamiento local?',
    sec1P1: 'Las cookies son pequeños archivos de texto que los sitios web guardan en su navegador. Algunas son estrictamente necesarias para el funcionamiento y otras nos ayudan a analizar el tráfico o mostrar contenido externo tras su consentimiento.',
    sec1P2: 'Además de cookies, el sitio web puede utilizar almacenamiento local (LocalStorage) para recordar el idioma elegido o su configuración de cookies.',
    sec2Title: '¿Qué categorías utilizamos?',
    catATitle: 'A. Tecnologías necesarias',
    catADesc: 'Las cookies y el almacenamiento necesario garantizan las funciones esenciales, la selección de idioma y el registro de sus preferencias de privacidad. No se pueden desactivar.',
    catBTitle: 'B. Cookies analíticas',
    catBDesc: 'Con su consentimiento, utilizamos Google Analytics 4 para evaluar estadísticas de tráfico y uso del sitio web, gestionadas mediante Google Tag Manager. Si las rechaza, no se guardan cookies analíticas.',
    catCTitle: 'C. Medios externos',
    catCDesc: 'El sitio web incluye vídeos de YouTube. El contenido externo sólo se carga tras su consentimiento. Si no lo autoriza, se muestra una vista previa con opción de activación voluntaria.',
    sec3Title: 'Resumen de tecnologías utilizadas',
    sec3Intro: 'La siguiente tabla detalla las tecnologías verificadas en la auditoría técnica del sitio:',
    thName: 'Nombre',
    thProvider: 'Proveedor',
    thStorageType: 'Tipo de almacenamiento',
    thPurpose: 'Propósito',
    thCategory: 'Categoría',
    thDuration: 'Duración',
    catBadgeNecessary: 'Necesarias',
    catBadgeAnalytics: 'Analíticas',
    catBadgeMedia: 'Medios externos',
    consentStorageType: 'Almacenamiento local del navegador (LocalStorage)',
    consentPurpose: 'Guarda preferencias de consentimiento (sed_cookie_consent_v2)',
    consentDuration: 'Persistente (hasta su eliminación en el navegador)',
    gaCookieType: 'Cookie HTTP',
    gaPurpose: 'Distingue usuarios únicos para estadísticas de visitas',
    gaDuration: '2 años (solo tras consentimiento)',
    ga4Purpose: 'Mantiene el estado de sesión y métricas en Google Analytics 4',
    ga4Duration: '2 años (solo tras consentimiento)',
    ytCookieType: 'Cookie HTTP / LocalStorage',
    ytPurpose: 'Reproducción de vídeos integrados con modo de privacidad mejorada',
    ytDuration: 'Sesión hasta 2 años (solo tras consentimiento)',
    sec4Title: '¿Cómo puede modificar sus preferencias?',
    sec4P1: 'En su primera visita puede aceptar todas las tecnologías opcionales, rechazarlas o configurar cada categoría.',
    sec4P2: 'Puede modificar su decisión en cualquier momento mediante el enlace "Configuración de cookies" en el pie de página o mediante el siguiente botón.',
    changeSettingsBtn: 'Abrir configuración de cookies',
    sec4P3: 'También puede eliminar cookies y datos del sitio mediante la configuración de su navegador web.',
    sec5Title: 'Responsable del sitio web y contacto',
    sec5P1: 'Responsable del sitio y tratamiento de datos:',
    companyName: 'JALUD Embedded s.r.o.',
    companyAddress: 'Nepomucká 1355/261, Černice, 326 00 Pilsen, República Checa',
    companyId: 'NIF / IČO: 03056287',
    contactEmail: 'Correo electrónico: info@jalud-embedded.com',
    privacyLinkText: 'Para más detalles sobre el tratamiento de datos personales, visite nuestra',
    privacyPageLink: 'Política de Privacidad',
    googleLinksIntro: 'Información de terceros:',
    googlePrivacyLink: 'Política de Privacidad de Google',
    googleCookieLink: 'Cómo utiliza Google las cookies y los datos'
  },
  pt: {
    eyebrow: 'PRIVACIDADE E COOKIES',
    title: 'Política de Cookies',
    subtitle: 'Informações sobre a utilização de cookies, armazenamento local e tecnologias externas no sítio jalud.eu.',
    sec1Title: 'O que são cookies e armazenamento local?',
    sec1P1: 'Cookies são pequenos ficheiros de texto guardados no seu navegador ao visitar páginas web. Alguns são estritamente necessários para o funcionamento e outros auxiliam na análise de tráfego e exibição de conteúdos com seu consentimento.',
    sec1P2: 'Além de cookies, o sítio web pode utilizar armazenamento local (LocalStorage), por exemplo para memorizar o idioma selecionado ou as preferências de privacidade.',
    sec2Title: 'Que categorias utilizamos?',
    catATitle: 'A. Tecnologias necessárias',
    catADesc: 'Garantem as funções fundamentais do sítio, memorização de idioma e opções de consentimento. Não podem ser desativadas no painel de cookies.',
    catBTitle: 'B. Cookies analíticos',
    catBDesc: 'Após o seu consentimento, utilizamos o Google Analytics 4 para avaliação estatística do tráfego através do Google Tag Manager. Se recusar, nenhuns cookies analíticos são armazenados.',
    catCTitle: 'C. Conteúdo multimédia externo',
    catCDesc: 'O sítio inclui vídeos alojados no YouTube. O conteúdo externo é carregado apenas após autorização. Caso não autorize, é apresentada uma pré-visualização estática com botão de ativação.',
    sec3Title: 'Visão geral das tecnologias utilizadas',
    sec3Intro: 'A tabela seguinte apresenta as tecnologias verificadas na auditoria técnica do sítio web:',
    thName: 'Nome',
    thProvider: 'Fornecedor',
    thStorageType: 'Tipo de armazenamento',
    thPurpose: 'Finalidade',
    thCategory: 'Categoria',
    thDuration: 'Validade',
    catBadgeNecessary: 'Necessário',
    catBadgeAnalytics: 'Analítico',
    catBadgeMedia: 'Média Externa',
    consentStorageType: 'Armazenamento local do navegador (LocalStorage)',
    consentPurpose: 'Memoriza as escolhas de consentimento (sed_cookie_consent_v2)',
    consentDuration: 'Persistente (até limpeza no navegador)',
    gaCookieType: 'Cookie HTTP',
    gaPurpose: 'Distingue utilizadores únicos para métricas de visita',
    gaDuration: '2 anos (apenas com consentimento)',
    ga4Purpose: 'Mantém o estado da sessão de navegação no Google Analytics 4',
    ga4Duration: '2 anos (apenas com consentimento)',
    ytCookieType: 'Cookie HTTP / LocalStorage',
    ytPurpose: 'Permite a reprodução de vídeos em modo de privacidade avançada',
    ytDuration: 'Sessão até 2 anos (apenas com consentimento)',
    sec4Title: 'Como pode alterar as suas preferências?',
    sec4P1: 'Na primeira visita pode aceitar todas as tecnologias opcionais, recusá-las ou configurá-las individualmente.',
    sec4P2: 'Pode alterar a sua escolha a qualquer momento através da ligação „Configurações de cookies“ no rodapé ou no botão abaixo.',
    changeSettingsBtn: 'Abrir configurações de cookies',
    sec4P3: 'Pode também eliminar cookies e dados do sítio através das opções de privacidade do seu navegador web.',
    sec5Title: 'Operador do sítio e contacto',
    sec5P1: 'Operador do sítio web e responsável pelo tratamento de dados:',
    companyName: 'JALUD Embedded s.r.o.',
    companyAddress: 'Nepomucká 1355/261, Černice, 326 00 Plzeň, República Checa',
    companyId: 'NIF / IČO: 03056287',
    contactEmail: 'E-mail: info@jalud-embedded.com',
    privacyLinkText: 'Para mais informações sobre o tratamento de dados pessoais, consulte a nossa',
    privacyPageLink: 'Política de Privacidade',
    googleLinksIntro: 'Informações de terceiros:',
    googlePrivacyLink: 'Política de Privacidade da Google',
    googleCookieLink: 'Como a Google utiliza cookies e dados'
  },
  ar: {
    eyebrow: 'الخصوصية وملفات تعريف الارتباط',
    title: 'سياسة ملفات تعريف الارتباط',
    subtitle: 'معلومات حول استخدام ملفات تعريف الارتباط والتخزين المحلي والوسائط الخارجية على موقع jalud.eu.',
    sec1Title: 'ما هي ملفات تعريف الارتباط والتخزين المحلي؟',
    sec1P1: 'ملفات تعريف الارتباط هي ملفات نصية صغيرة يخزنها المتصفح عند زيارة المواقع الإلكترونية. بعضها ضروري لتشغيل الموقع، والبعض الآخر يساعد في قياس حركة المرور أو عرض الوسائط الخارجية بعد موافقتك.',
    sec1P2: 'بالإضافة إلى ملفات تعريف الارتباط، قد يستخدم الموقع التخزين المحلي للمتصفح (LocalStorage)، على سبيل المثال لتذكر اللغة المحددة أو تفضيلات الخصوصية.',
    sec2Title: 'ما هي الفئات التي نستخدمها؟',
    catATitle: 'أ. التقنيات الضرورية',
    catADesc: 'تخدم ملفات تعريف الارتباط الضرورية والتخزين المحلي التشغيل الأساسي للموقع وتذكر تفضيلات اللغة وحفظ إعدادات الخصوصية. لا يمكن تعطيل هذه التقنيات لأن أجزاء من الموقع لن تعمل بشكل صحيح بدونها.',
    catBTitle: 'ب. ملفات تعريف الارتباط التحليلية',
    catBDesc: 'بعد الحصول على موافقتك، نستخدم Google Analytics 4 للتقييم الإحصائي لحركة المرور واستخدام الموقع من خلال Google Tag Manager. إذا رفضتها، فلن يتم تخزين أي ملفات تحليلية.',
    catCTitle: 'ج. الوسائط الخارجية',
    catCDesc: 'يحتوي الموقع على مقاطع فيديو مقدمة من خدمة YouTube. لا يتم تحميل المحتوى الخارجي إلا بعد موافقتك. إذا لم توافق، يظهر للمستخدم معاينة بديلة مع خيار التمكين.',
    sec3Title: 'نظرة عامة على التقنيات المستخدمة',
    sec3Intro: 'يوضح الجدول التالي التقنيات التي تم التحقق منها عبر التدقيق التقني الفعلي للموقع:',
    thName: 'الاسم',
    thProvider: 'المزود',
    thStorageType: 'نوع التخزين',
    thPurpose: 'الغرض',
    thCategory: 'الفئة',
    thDuration: 'مدة الصلاحية',
    catBadgeNecessary: 'ضرورية',
    catBadgeAnalytics: 'تحليلية',
    catBadgeMedia: 'وسائط خارجية',
    consentStorageType: 'التخزين المحلي للمتصفح (LocalStorage)',
    consentPurpose: 'تذكر تفضيلات الموافقة على ملفات تعريف الارتباط (sed_cookie_consent_v2)',
    consentDuration: 'دائم (حتى الحذف من المتصفح)',
    gaCookieType: 'HTTP Cookie',
    gaPurpose: 'تمييز الزوار الفريدين لإحصاءات حركة المرور',
    gaDuration: 'سنتان (فقط بعد الموافقة)',
    ga4Purpose: 'الحفاظ على حالة الجلسة وتجميع المقاييس في Google Analytics 4',
    ga4Duration: 'سنتان (فقط بعد الموافقة)',
    ytCookieType: 'HTTP Cookie / LocalStorage',
    ytPurpose: 'تشغيل مقاطع الفيديو المضمنة في وضع الخصوصية المحسنة وحفظ تفضيلات المشغل',
    ytDuration: 'جلسة حتى سنتين (فقط بعد الموافقة)',
    sec4Title: 'كيف يمكنك تغيير إعداداتك؟',
    sec4P1: 'عند زيارتك الأولى للموقع، يمكنك قبول جميع التقنيات الاختيارية أو رفضها أو ضبط كل فئة على حدة.',
    sec4P2: 'يمكنك تغيير قرارك في أي وقت عبر رابط "إعدادات ملفات تعريف الارتباط" في أسفل الموقع أو بالنقر على الزر أدناه.',
    changeSettingsBtn: 'فتح إعدادات ملفات تعريف الارتباط',
    sec4P3: 'يمكنك أيضاً حذف ملفات تعريف الارتباط وبيانات الموقع عبر إعدادات متصفحك في أي وقت.',
    sec5Title: 'مشغل الموقع ومعلومات الاتصال',
    sec5P1: 'مشغل الموقع والمسؤول عن معالجة البيانات الشخصية:',
    companyName: 'JALUD Embedded s.r.o.',
    companyAddress: 'Nepomucká 1355/261, Černice, 326 00 Plzeň, جمهورية التشيك',
    companyId: 'رقم التسجيل (IČO): 03056287',
    contactEmail: 'البريد الإلكتروني: info@jalud-embedded.com',
    privacyLinkText: 'لمزيد من المعلومات حول معالجة البيانات الشخصية، يرجى زيارة صفحة',
    privacyPageLink: 'سياسة الخصوصية',
    googleLinksIntro: 'معلومات الأطراف الثالثة:',
    googlePrivacyLink: 'سياسة خصوصية Google',
    googleCookieLink: 'كيفية استخدام Google لملفات تعريف الارتباط والبيانات'
  }
};

export function renderCookiePolicyPage(onNavigate) {
  const page = document.createElement('div');
  page.className = 'sed-page-cookie-policy';
  const lang = getLocale() || 'en';
  const t = cookiePolicyTranslations[lang] || cookiePolicyTranslations.en;
  const isRtl = lang === 'ar';

  page.innerHTML = `
    <!-- Header Hero -->
    <section class="sed-section" style="background: linear-gradient(165deg, var(--blue-light) 0%, #ffffff 70%); padding: 4.5rem 0 3rem; direction: ${isRtl ? 'rtl' : 'ltr'}; text-align: ${isRtl ? 'right' : 'left'};">
      <div class="sed-container">
        <div class="sed-section-header" style="max-width: 860px; margin-bottom: 2rem;">
          <div class="sed-eyebrow" style="color: var(--blue-primary); font-weight: 800; letter-spacing: 0.08em; font-size: 0.85rem; margin-bottom: 0.75rem;">${t.eyebrow}</div>
          <h1 class="sed-page-hero-title" style="font-size: clamp(2rem, 4vw, 2.75rem); color: var(--navy-dark); font-weight: 800; line-height: 1.2; margin-bottom: 1rem;">${t.title}</h1>
          <p class="sed-section-desc" style="font-size: 1.1rem; color: var(--text-muted); line-height: 1.6;">
            ${t.subtitle}
          </p>
        </div>
      </div>
    </section>

    <!-- Content Section -->
    <section class="sed-section" style="background-color: #ffffff; padding: 2rem 0 5rem; direction: ${isRtl ? 'rtl' : 'ltr'}; text-align: ${isRtl ? 'right' : 'left'};">
      <div class="sed-container" style="max-width: 960px;">
        <div class="sed-card" style="padding: 2.5rem; line-height: 1.8; color: var(--text-dark); border: 1.5px solid var(--slate-line); border-radius: 16px; box-shadow: 0 4px 24px rgba(0,0,0,0.04); background: #ffffff;">
          
          <!-- Section 1 -->
          <h2 style="font-family: var(--font-heading); font-size: 1.35rem; color: var(--navy-dark); margin-bottom: 0.85rem; font-weight: 800; display: flex; align-items: center; gap: 10px;">
            <span style="background: var(--blue-ultra-light); color: var(--blue-primary); width: 30px; height: 30px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.9rem; font-weight: 800; flex-shrink: 0;">1</span>
            <span>${t.sec1Title}</span>
          </h2>
          <p style="margin-bottom: 1rem; color: #334155;">
            ${t.sec1P1}
          </p>
          <p style="margin-bottom: 2rem; color: #334155;">
            ${t.sec1P2}
          </p>

          <hr style="border: 0; border-top: 1px solid var(--slate-line); margin: 2rem 0;" />

          <!-- Section 2 -->
          <h2 style="font-family: var(--font-heading); font-size: 1.35rem; color: var(--navy-dark); margin-bottom: 1.25rem; font-weight: 800; display: flex; align-items: center; gap: 10px;">
            <span style="background: var(--blue-ultra-light); color: var(--blue-primary); width: 30px; height: 30px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.9rem; font-weight: 800; flex-shrink: 0;">2</span>
            <span>${t.sec2Title}</span>
          </h2>
          
          <div style="margin-bottom: 1.25rem; padding: 1.25rem 1.5rem; background: var(--blue-ultra-light, #f0f7ff); border-${isRtl ? 'right' : 'left'}: 4px solid var(--blue-primary); border-radius: 8px;">
            <strong style="color: var(--navy-dark); font-size: 1rem;">${t.catATitle}</strong>
            <p style="margin: 0.5rem 0 0; font-size: 0.92rem; line-height: 1.65; color: #334155;">
              ${t.catADesc}
            </p>
          </div>

          <div style="margin-bottom: 1.25rem; padding: 1.25rem 1.5rem; background: var(--blue-ultra-light, #f0f7ff); border-${isRtl ? 'right' : 'left'}: 4px solid var(--blue-primary); border-radius: 8px;">
            <strong style="color: var(--navy-dark); font-size: 1rem;">${t.catBTitle}</strong>
            <p style="margin: 0.5rem 0 0; font-size: 0.92rem; line-height: 1.65; color: #334155;">
              ${t.catBDesc}
            </p>
          </div>

          <div style="margin-bottom: 2rem; padding: 1.25rem 1.5rem; background: var(--blue-ultra-light, #f0f7ff); border-${isRtl ? 'right' : 'left'}: 4px solid var(--blue-primary); border-radius: 8px;">
            <strong style="color: var(--navy-dark); font-size: 1rem;">${t.catCTitle}</strong>
            <p style="margin: 0.5rem 0 0; font-size: 0.92rem; line-height: 1.65; color: #334155;">
              ${t.catCDesc}
            </p>
          </div>

          <hr style="border: 0; border-top: 1px solid var(--slate-line); margin: 2rem 0;" />

          <!-- Section 3: Verified Audit Table -->
          <h2 style="font-family: var(--font-heading); font-size: 1.35rem; color: var(--navy-dark); margin-bottom: 0.85rem; font-weight: 800; display: flex; align-items: center; gap: 10px;">
            <span style="background: var(--blue-ultra-light); color: var(--blue-primary); width: 30px; height: 30px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.9rem; font-weight: 800; flex-shrink: 0;">3</span>
            <span>${t.sec3Title}</span>
          </h2>
          <p style="margin-bottom: 1.25rem; color: #334155; font-size: 0.95rem;">
            ${t.sec3Intro}
          </p>

          <div style="overflow-x: auto; margin-bottom: 2.25rem; border: 1px solid var(--slate-line); border-radius: 10px;">
            <table style="width: 100%; border-collapse: collapse; font-size: 0.85rem; text-align: ${isRtl ? 'right' : 'left'};">
              <thead>
                <tr style="background-color: var(--navy-dark); color: #ffffff;">
                  <th style="padding: 10px 14px; border-bottom: 1px solid #1e293b;">${t.thName}</th>
                  <th style="padding: 10px 14px; border-bottom: 1px solid #1e293b;">${t.thProvider}</th>
                  <th style="padding: 10px 14px; border-bottom: 1px solid #1e293b;">${t.thStorageType}</th>
                  <th style="padding: 10px 14px; border-bottom: 1px solid #1e293b;">${t.thPurpose}</th>
                  <th style="padding: 10px 14px; border-bottom: 1px solid #1e293b;">${t.thCategory}</th>
                  <th style="padding: 10px 14px; border-bottom: 1px solid #1e293b; white-space: nowrap;">${t.thDuration}</th>
                </tr>
              </thead>
              <tbody>
                <!-- Item 1: LocalStorage Consent -->
                <tr style="border-bottom: 1px solid var(--slate-line); background-color: #f8fafc;">
                  <td style="padding: 10px 14px; font-weight: 700; font-family: monospace; color: var(--navy-dark);">sed_cookie_consent_v2</td>
                  <td style="padding: 10px 14px; color: #475569;">jalud.eu</td>
                  <td style="padding: 10px 14px; color: #475569;">${t.consentStorageType}</td>
                  <td style="padding: 10px 14px; color: #334155;">${t.consentPurpose}</td>
                  <td style="padding: 10px 14px;"><span style="background: rgba(53, 168, 223, 0.15); color: #0284c7; padding: 3px 8px; border-radius: 4px; font-weight: 700; font-size: 0.75rem;">${t.catBadgeNecessary}</span></td>
                  <td style="padding: 10px 14px; color: #475569;">${t.consentDuration}</td>
                </tr>

                <!-- Item 2: _ga -->
                <tr style="border-bottom: 1px solid var(--slate-line);">
                  <td style="padding: 10px 14px; font-weight: 700; font-family: monospace; color: var(--navy-dark);">_ga</td>
                  <td style="padding: 10px 14px; color: #475569;">Google Analytics (jalud.eu)</td>
                  <td style="padding: 10px 14px; color: #475569;">${t.gaCookieType}</td>
                  <td style="padding: 10px 14px; color: #334155;">${t.gaPurpose}</td>
                  <td style="padding: 10px 14px;"><span style="background: rgba(16, 185, 129, 0.15); color: #059669; padding: 3px 8px; border-radius: 4px; font-weight: 700; font-size: 0.75rem;">${t.catBadgeAnalytics}</span></td>
                  <td style="padding: 10px 14px; color: #475569;">${t.gaDuration}</td>
                </tr>

                <!-- Item 3: _ga_3YZ790ZBSX -->
                <tr style="border-bottom: 1px solid var(--slate-line); background-color: #f8fafc;">
                  <td style="padding: 10px 14px; font-weight: 700; font-family: monospace; color: var(--navy-dark);">_ga_3YZ790ZBSX</td>
                  <td style="padding: 10px 14px; color: #475569;">Google Analytics (jalud.eu)</td>
                  <td style="padding: 10px 14px; color: #475569;">${t.gaCookieType}</td>
                  <td style="padding: 10px 14px; color: #334155;">${t.ga4Purpose}</td>
                  <td style="padding: 10px 14px;"><span style="background: rgba(16, 185, 129, 0.15); color: #059669; padding: 3px 8px; border-radius: 4px; font-weight: 700; font-size: 0.75rem;">${t.catBadgeAnalytics}</span></td>
                  <td style="padding: 10px 14px; color: #475569;">${t.ga4Duration}</td>
                </tr>

                <!-- Item 4: YouTube Cookies -->
                <tr>
                  <td style="padding: 10px 14px; font-weight: 700; font-family: monospace; color: var(--navy-dark);">CONSENT, SOCS, YSC, VISITOR_INFO1_LIVE</td>
                  <td style="padding: 10px 14px; color: #475569;">YouTube (youtube-nocookie.com)</td>
                  <td style="padding: 10px 14px; color: #475569;">${t.ytCookieType}</td>
                  <td style="padding: 10px 14px; color: #334155;">${t.ytPurpose}</td>
                  <td style="padding: 10px 14px;"><span style="background: rgba(239, 68, 68, 0.15); color: #dc2626; padding: 3px 8px; border-radius: 4px; font-weight: 700; font-size: 0.75rem;">${t.catBadgeMedia}</span></td>
                  <td style="padding: 10px 14px; color: #475569;">${t.ytDuration}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <hr style="border: 0; border-top: 1px solid var(--slate-line); margin: 2rem 0;" />

          <!-- Section 4 -->
          <h2 style="font-family: var(--font-heading); font-size: 1.35rem; color: var(--navy-dark); margin-bottom: 0.85rem; font-weight: 800; display: flex; align-items: center; gap: 10px;">
            <span style="background: var(--blue-ultra-light); color: var(--blue-primary); width: 30px; height: 30px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.9rem; font-weight: 800; flex-shrink: 0;">4</span>
            <span>${t.sec4Title}</span>
          </h2>
          <p style="margin-bottom: 0.85rem; color: #334155;">
            ${t.sec4P1}
          </p>
          <p style="margin-bottom: 1.25rem; color: #334155;">
            ${t.sec4P2}
          </p>
          
          <div style="margin-bottom: 1.5rem;">
            <button id="sed-cookie-policy-reopen-btn" class="sed-btn" style="background-color: var(--blue-primary); color: #ffffff; padding: 0.65rem 1.4rem; border-radius: 8px; font-weight: 700; font-size: 0.92rem; border: none; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; box-shadow: 0 2px 8px rgba(53, 168, 223, 0.3);">
              <span>⚙️</span> ${t.changeSettingsBtn}
            </button>
          </div>

          <p style="margin-bottom: 2rem; color: #475569; font-size: 0.92rem;">
            ${t.sec4P3}
          </p>

          <hr style="border: 0; border-top: 1px solid var(--slate-line); margin: 2rem 0;" />

          <!-- Section 5 -->
          <h2 style="font-family: var(--font-heading); font-size: 1.35rem; color: var(--navy-dark); margin-bottom: 0.85rem; font-weight: 800; display: flex; align-items: center; gap: 10px;">
            <span style="background: var(--blue-ultra-light); color: var(--blue-primary); width: 30px; height: 30px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.9rem; font-weight: 800; flex-shrink: 0;">5</span>
            <span>${t.sec5Title}</span>
          </h2>
          <p style="margin-bottom: 0.5rem; color: #334155;">${t.sec5P1}</p>
          <div style="padding: 1.25rem; background: #f8fafc; border: 1px solid var(--slate-line); border-radius: 8px; margin-bottom: 1.25rem; font-size: 0.92rem; line-height: 1.65; color: #1e293b;">
            <strong>${t.companyName}</strong><br />
            ${t.companyAddress}<br />
            ${t.companyId}<br />
            <strong>${t.contactEmail}</strong>
          </div>

          <p style="margin-bottom: 1rem; color: #334155; font-size: 0.92rem;">
            ${t.privacyLinkText} <a href="#privacy-policy" id="sed-cookie-to-privacy" style="color: var(--blue-primary); font-weight: 700; text-decoration: underline;">${t.privacyPageLink}</a>.
          </p>

          <div style="padding-top: 1rem; border-top: 1px solid var(--slate-line); font-size: 0.86rem; color: #64748b;">
            <strong>${t.googleLinksIntro}</strong><br />
            • <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style="color: var(--blue-primary); text-decoration: underline;">${t.googlePrivacyLink} ↗</a><br />
            • <a href="https://policies.google.com/technologies/cookies" target="_blank" rel="noopener noreferrer" style="color: var(--blue-primary); text-decoration: underline;">${t.googleCookieLink} ↗</a>
          </div>

        </div>
      </div>
    </section>
  `;

  // Attach button click to open settings modal
  const reopenBtn = page.querySelector('#sed-cookie-policy-reopen-btn');
  if (reopenBtn) {
    reopenBtn.addEventListener('click', (e) => {
      e.preventDefault();
      openCookieSettings(onNavigate);
    });
  }

  // Attach internal privacy link
  const privacyLink = page.querySelector('#sed-cookie-to-privacy');
  if (privacyLink) {
    privacyLink.addEventListener('click', (e) => {
      e.preventDefault();
      if (typeof onNavigate === 'function') {
        onNavigate('privacy-policy');
      } else {
        window.location.hash = 'privacy-policy';
      }
    });
  }

  return page;
}
